import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import type { RootState } from "@react-three/fiber";
import {
  Mesh,
  Vector3,
  BufferGeometry,
  LineBasicMaterial,
  Line,
  Float32BufferAttribute,
  EdgesGeometry,
  LineSegments,
  BoxGeometry,
  Group
} from "three";
import type { VectorDBPoint, VectorDBCubeProps } from "@/types/components";

const DEFAULT_CONFIG = {
  maxConnections: 3,
  connectionDistance: 0.8,
  connectionDuration: 3,
  pointCount: 30,
  pointSize: 0.03,
  cubeSize: 2,
  rotationSpeed: 0.2,
} as const;

const clampPosition = (position: Vector3, bounds: number): Vector3 => {
  const halfBounds = bounds / 2 - 0.1;
  return new Vector3(
    Math.max(-halfBounds, Math.min(halfBounds, position.x)),
    Math.max(-halfBounds, Math.min(halfBounds, position.y)),
    Math.max(-halfBounds, Math.min(halfBounds, position.z))
  );
};

const VectorDBCube: React.FC<VectorDBCubeProps> = ({
  maxConnections = DEFAULT_CONFIG.maxConnections,
  connectionDistance = DEFAULT_CONFIG.connectionDistance,
  connectionDuration = DEFAULT_CONFIG.connectionDuration,
  pointCount = DEFAULT_CONFIG.pointCount,
  pointSize = DEFAULT_CONFIG.pointSize,
  cubeSize = DEFAULT_CONFIG.cubeSize,
  rotationSpeed = DEFAULT_CONFIG.rotationSpeed,
}) => {
  const groupRef = useRef<Group>(null);
  const linesRef = useRef<Line>(null);
  const cubeEdgesRef = useRef<LineSegments>(null);

  const halfSize = cubeSize / 2 - 0.1;

  const points = useMemo<VectorDBPoint[]>(() => {
    return Array(pointCount)
      .fill(null)
      .map(() => ({
        position: new Vector3(
          (Math.random() * 2 - 1) * halfSize,
          (Math.random() * 2 - 1) * halfSize,
          (Math.random() * 2 - 1) * halfSize
        ),
        speed: Math.random() * 0.02 + 0.02,
        offset: Math.random() * Math.PI * 2,
        connections: [],
        connectionTimer: Math.random() * connectionDuration,
      }));
  }, [pointCount, connectionDuration, halfSize]);

  const lineGeometry = useMemo(() => new BufferGeometry(), []);
  const lineMaterial = useMemo(
    () => new LineBasicMaterial({ color: 0xffffff, opacity: 0.5, transparent: true }),
    []
  );

  useFrame((state: RootState, delta: number) => {
    if (groupRef.current) {
      groupRef.current.rotation.x += delta * rotationSpeed;
      groupRef.current.rotation.y += delta * rotationSpeed;
    }

    points.forEach((point) => {
      const newY = Math.sin(state.clock.elapsedTime * point.speed + point.offset) * 0.6;
      point.position.y = Math.max(-halfSize, Math.min(halfSize, point.position.y + newY * delta));
    });

    points.forEach((point, i) => {
      point.connectionTimer -= delta;

      if (point.connectionTimer <= 0) {
        point.connectionTimer = Math.random() * connectionDuration;
        point.connections = [];

        const availablePoints = points
          .map((p, idx) => ({ distance: point.position.distanceTo(p.position), index: idx }))
          .filter(
            (p) =>
              p.index !== i &&
              p.distance < connectionDistance &&
              points[p.index].connections.length < maxConnections &&
              Math.abs(point.position.x - points[p.index].position.x) < connectionDistance &&
              Math.abs(point.position.y - points[p.index].position.y) < connectionDistance &&
              Math.abs(point.position.z - points[p.index].position.z) < connectionDistance
          )
          .sort((a, b) => a.distance - b.distance)
          .slice(0, maxConnections);

        point.connections = availablePoints.map((p) => p.index);
        availablePoints.forEach((p) => {
          points[p.index].connections.push(i);
        });
      }
    });

    const positions: number[] = [];
    const uniqueConnections = new Set<string>();

    points.forEach((point, i) => {
      point.connections.forEach((connectionIndex) => {
        const connectionKey = [i, connectionIndex].sort().join('-');
        if (!uniqueConnections.has(connectionKey)) {
          uniqueConnections.add(connectionKey);

          const startPoint = clampPosition(point.position.clone(), cubeSize);
          const endPoint = clampPosition(points[connectionIndex].position.clone(), cubeSize);

          positions.push(
            startPoint.x, startPoint.y, startPoint.z,
            endPoint.x, endPoint.y, endPoint.z
          );
        }
      });
    });

    lineGeometry.setAttribute(
      'position',
      new Float32BufferAttribute(positions, 3)
    );
  });

  return (
    <group ref={groupRef}>
      <mesh>
        <boxGeometry args={[cubeSize, cubeSize, cubeSize]} />
        <meshStandardMaterial
          color="#00FFFF"
          opacity={0.3}
          wireframe
          transparent
          emissive="#00FFFF"
          emissiveIntensity={0.3}
        />
      </mesh>
      {/* Create multiple offset line segments for thicker appearance */}
      {[-0.002, -0.001, 0, 0.001, 0.002].map((offset, i) => (
        <lineSegments key={i} position={[offset, offset, offset]}>
          <edgesGeometry args={[new BoxGeometry(cubeSize, cubeSize, cubeSize)]} />
          <lineBasicMaterial color="#00FFFF" />
        </lineSegments>
      ))}
      <group>
        {points.map((point, i) => (
          <mesh key={i} position={point.position}>
            <sphereGeometry args={[pointSize, 16, 16]} />
            <meshBasicMaterial color="#FFFFFF" />
          </mesh>
        ))}
        <primitive object={new Line(lineGeometry, lineMaterial)} />
      </group>
    </group>
  );
};

export default VectorDBCube;
